import { useEffect, useState } from 'react';
import { Car, MapPin, RefreshCw, Search, ToggleLeft, ToggleRight, Truck, Wallet } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface Opportunity {
  id: number;
  opportunityType: string;
  area: string;
  requiredVehicleType: string;
  packageCount: number;
  payoutAmount: number;
  expiresAt?: string;
}

export default function DriverOpportunitiesPage() {
  const { user } = useAuth();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [area, setArea] = useState('');
  const [vehicleType, setVehicleType] = useState('');
  const [available, setAvailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const load = async () => {
    setLoading(true);
    setMessage('');
    try {
      const response = await api.get('/api/driver-opportunities', { params: { area: area || undefined, vehicleType: vehicleType || undefined } });
      setOpportunities(response.data?.data || []);
    } catch {
      setMessage('تعذر تحميل الفرص. تحقق من تسجيل الدخول وصلاحية الشركة.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const claim = async (id: number) => {
    try {
      await api.post(`/api/driver-opportunities/${id}/claim`, {});
      setMessage('تم قبول الفرصة بنجاح. ستظهر المهمة ضمن مهامك بعد تحديث الصفحة.');
      await load();
    } catch (error: any) {
      setMessage(error?.response?.data?.message || 'لم يتم قبول الفرصة، ربما حجزها مندوب آخر.');
    }
  };

  const toggleAvailability = async () => {
    const nextValue = !available;
    try {
      await api.put('/api/driver-opportunities/me/availability', { isAvailable: nextValue, availableMinutes: 480 });
      setAvailable(nextValue);
      setMessage(nextValue ? 'أصبحت متاحًا الآن لاستقبال الفرص.' : 'تم إيقاف استقبال الفرص.');
    } catch (error: any) {
      setMessage(error?.response?.data?.message || 'تعذر تحديث حالة التوافر.');
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6" dir="rtl">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">فرص العمل للمندوبين</h1>
          <p className="text-muted-foreground mt-1">اختر المهام القريبة منك حسب المنطقة ونوع المركبة.</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Truck className="h-5 w-5" /> {user?.username || 'حساب المندوب'}</div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <input className="h-10 rounded-md border px-3" value={area} onChange={e => setArea(e.target.value)} placeholder="المنطقة" />
            <select className="h-10 rounded-md border px-3" value={vehicleType} onChange={e => setVehicleType(e.target.value)}>
              <option value="">كل المركبات</option><option value="motorcycle">دراجة نارية</option><option value="car">سيارة</option><option value="van">فان</option>
            </select>
            <Button onClick={() => void toggleAvailability()} variant={available ? 'default' : 'outline'}>{available ? <ToggleRight className="ml-2" /> : <ToggleLeft className="ml-2" />}{available ? 'متاح الآن' : 'غير متاح'}</Button>
            <Button onClick={() => void load()} disabled={loading}><Search className="ml-2 h-4 w-4" />بحث</Button>
          </div>
        </CardContent>
      </Card>

      {message && <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-blue-800">{message}</div>}
      <div className="flex items-center justify-between"><h2 className="text-lg font-semibold">الفرص المتاحة ({opportunities.length})</h2><Button variant="ghost" onClick={() => void load()} disabled={loading}><RefreshCw className="h-4 w-4" /></Button></div>
      {opportunities.length === 0 ? <Card><CardContent className="py-12 text-center text-muted-foreground">لا توجد فرص مطابقة حاليًا.</CardContent></Card> : <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{opportunities.map(item => <Card key={item.id}><CardHeader><CardTitle className="flex items-center justify-between"><span>{item.opportunityType === 'pickup' ? 'التقاط شحنة' : 'توصيل طلب'}</span><span className="text-emerald-700 flex items-center gap-1"><Wallet className="h-4 w-4" />{item.payoutAmount} ج.م</span></CardTitle></CardHeader><CardContent><div className="grid grid-cols-2 gap-3 text-sm"><div className="flex gap-2"><MapPin className="h-4 w-4 text-blue-600" />{item.area}</div><div className="flex gap-2"><Car className="h-4 w-4 text-blue-600" />{item.requiredVehicleType === 'motorcycle' ? 'دراجة' : item.requiredVehicleType === 'car' ? 'سيارة' : 'أي مركبة'}</div><div>عدد الطرود: {item.packageCount}</div><div>الفرصة رقم: {item.id}</div></div><Button className="w-full mt-5" onClick={() => void claim(item.id)} disabled={!available}>قبول الفرصة</Button>{!available && <p className="text-xs text-muted-foreground mt-2 text-center">فعّل وضع متاح الآن لقبول المهمة.</p>}</CardContent></Card>)}</div>}
    </div>
  );
}
